import React, { useMemo } from 'react'
import Chart from 'react-apexcharts'
import { Card, CardBody, Col, Row } from 'reactstrap'
import { newFormatThousandSeparator } from 'utils/formatNumber'

const CashFlowInsights = ({ dataCashFlow }) => {
  const items = dataCashFlow?.items || []

  const { incomeSourceData, expenseCategoryData, partnerData } = useMemo(() => {
    const incomeSource = {}
    const expenseCategory = {}
    const partners = {}

    items.forEach((item) => {
      const isIncome = item?.incomeorexpense === 'income'
      const isExpense = item?.incomeorexpense === 'expense'
      const label = item?.label || 'Lainnya'
      const amount = item?.amount || 0
      const partner = item?.extradata?.picname || 'PasarMIKRO'

      if (isIncome) {
        incomeSource[label] = (incomeSource[label] || 0) + amount
      } else if (isExpense) {
        expenseCategory[label] = (expenseCategory[label] || 0) + amount
      }

      if (!partners[partner]) {
        partners[partner] = { income: 0, expense: 0, net: 0 }
      }

      if (isIncome) {
        partners[partner].income += amount
        partners[partner].net += amount
      } else if (isExpense) {
        partners[partner].expense += amount
        partners[partner].net -= amount
      }
    })

    return {
      incomeSourceData: incomeSource,
      expenseCategoryData: expenseCategory,
      partnerData: partners,
    }
  }, [items])

  // Chart configs
  // Income Pie
  const incomeLabels = Object.keys(incomeSourceData)
  const incomeSeries = Object.values(incomeSourceData)
  const incomeOptions = {
    chart: { type: 'donut', toolbar: { show: false } },
    labels: incomeLabels,
    colors: ['#039855', '#32D583', '#73E2A3', '#A6F4C5', '#D1FADF'],
    tooltip: {
      y: { formatter: (val) => `Rp${newFormatThousandSeparator(val)}` },
    },
    legend: { position: 'bottom' },
    dataLabels: { enabled: false },
  }

  // Expense Pie
  const expenseLabels = Object.keys(expenseCategoryData)
  const expenseSeries = Object.values(expenseCategoryData)
  const expenseOptions = {
    chart: { type: 'donut', toolbar: { show: false } },
    labels: expenseLabels,
    colors: ['#D92D20', '#F04438', '#F97066', '#FDA29B', '#FECDCA'],
    tooltip: {
      y: { formatter: (val) => `Rp${newFormatThousandSeparator(val)}` },
    },
    legend: { position: 'bottom' },
    dataLabels: { enabled: false },
  }

  // Partner Bar
  const partnerLabels = Object.keys(partnerData)
  const partnerIncomeSeries = partnerLabels.map((p) => partnerData[p].income)
  const partnerExpenseSeries = partnerLabels.map((p) => partnerData[p].expense)
  const partnerNetSeries = partnerLabels.map((p) => partnerData[p].net)

  const partnerOptions = {
    chart: { type: 'bar', toolbar: { show: false }, stacked: false },
    plotOptions: { bar: { horizontal: true, borderRadius: 2 } },
    xaxis: {
      categories: partnerLabels,
      labels: { formatter: (val) => newFormatThousandSeparator(val) },
    },
    colors: ['#039855', '#D92D20', '#007AFF'],
    tooltip: {
      y: { formatter: (val) => `Rp${newFormatThousandSeparator(val)}` },
    },
    legend: { position: 'bottom' },
    dataLabels: { enabled: false },
  }

  const partnerSeries = [
    { name: 'Pemasukan', data: partnerIncomeSeries },
    { name: 'Pengeluaran', data: partnerExpenseSeries },
    { name: 'Net', data: partnerNetSeries },
  ]

  if (!items.length) return null

  return (
    <div className="mb-4 mt-2">
      <Row className="gy-3 mb-3">
        <Col xs="12" md="6">
          <Card className="h-100 border shadow-sm">
            <CardBody>
              <h6 className="fw-semibold mb-3">Sumber Pemasukan</h6>
              {incomeSeries.length > 0 ? (
                <Chart
                  options={incomeOptions}
                  series={incomeSeries}
                  type="donut"
                  height={300}
                />
              ) : (
                <p className="text-muted text-center mt-5">Belum ada data</p>
              )}
            </CardBody>
          </Card>
        </Col>
        <Col xs="12" md="6">
          <Card className="h-100 border shadow-sm">
            <CardBody>
              <h6 className="fw-semibold mb-3">Pengeluaran Berdasarkan Kategori</h6>
              {expenseSeries.length > 0 ? (
                <Chart
                  options={expenseOptions}
                  series={expenseSeries}
                  type="donut"
                  height={300}
                />
              ) : (
                <p className="text-muted text-center mt-5">Belum ada data</p>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col xs="12">
          <Card className="border shadow-sm">
            <CardBody>
              <h6 className="fw-semibold mb-3">Transaksi per Mitra</h6>
              {partnerLabels.length > 0 ? (
                <Chart
                  options={partnerOptions}
                  series={partnerSeries}
                  type="bar"
                  height={Math.max(300, partnerLabels.length * 60)}
                />
              ) : (
                <p className="text-muted text-center mt-5">Belum ada data</p>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default CashFlowInsights
